import React, { useEffect, useState } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";

import {
  Content,
  TitleContainer,
  FormContainer,
  ButtonContainer,
  CheckboxContainer,
} from "./styles";

import { FiX } from "react-icons/fi";
import { RiArrowLeftRightLine } from "react-icons/ri";

import refreshToken from "../../utils/refreshToken";
import api from "../../services/api";
import * as Yup from "yup";
import getValidationErrors from "../../utils/getValidationError";
import { getApiErrorToastTitle } from "../../utils/apiErrorToast";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import { useRefresh } from "../../hooks/refresh";
import OutsideClickHandler from "react-outside-click-handler";
import Select from "react-select";

interface IConsultant {
  id: number | null;
  first_name: string;
}

interface IParam {
  scheduleId: number;
  schedule: string;
  consultants: IConsultant[];
  video_call?: boolean;
}

interface IModalConsult {
  open: boolean;
  functionToClose(): void;
  param: IParam;
}

interface IDate {
  id: number;
  date: string;
  consultant_id: number;
}

const ORIGIN_SOURCES = [
  "OLX",
  "Facebook",
  "ZAP imóveis",
  "Viva Real",
  "Google",
  "Instagram",
  "Outro",
] as const;

type OriginSource = typeof ORIGIN_SOURCES[number];
const ORIGIN_SOURCE_OPTIONS = ORIGIN_SOURCES.map((s) => ({ value: s, label: s }));


const normalizeScheduleDate = (dateString: string): string => {
  if (dateString.includes('|')) {
    return dateString.split('|')[0] + ' ' + dateString.split('|')[1];
  } else if (dateString.includes('T')) {
    return dateString.replace('T', ' ');
  }
  return dateString;
};

function setFetchedDatesToSelectFields(dates: IDate[] = []) {
  const dateFields = dates.map((date) => date.date.substring(0, 10));
  const uniques = Array.from(new Set(dateFields));
  const orderDates = uniques.sort((a, b) => +new Date(a) - +new Date(b));
  const formatDate = orderDates.map((date, index) => {
    return {
      value: orderDates[index],
      label: `${date.substring(8, 10)}/${date.substring(5, 7)}/${date.substring(
        0,
        4
      )}`,
    };
  });

  return formatDate;
}

const ModalUpdate: React.FC<IModalConsult> = ({
  open,
  functionToClose,
  param,
}: IModalConsult) => {
  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();
  const { handleRefresh } = useRefresh();

  const canManageSchedules = user?.role !== "consultant";

  useEffect(() => {
    if (!open || canManageSchedules) {
      return;
    }
    addToast({
      title: "Sem permissão para alterar a agenda",
      type: "error",
    });
    functionToClose();
  }, [open, canManageSchedules, addToast, functionToClose]);

  const [originSource, setOriginSource] = useState<OriginSource | "">("");
  const [videoCall, setVideoCall] = useState(false);

  const { schedule, consultants, scheduleId } = param;

  const [datePart, timePart, consultantId] = schedule.split('|');
  const formattedTime = timePart.length > 5 ? timePart.substring(0, 5) : timePart;

  const [scheduleDestination, setScheduleDestination] = useState({
    consultantId: consultantId,
    date: datePart,
    hour: formattedTime + ':00',
  });

  const [availableDates, setAvailableDates] = useState<IDate[]>([]);
  const [showConsultantsList, setShowConsultantsList] = useState(false);
  const [loadingRequestToUpdate, setLoadingRequestToUpdate] = useState(false);

  const selectedConsultant = consultants.find(
    (consultant) => +scheduleDestination.consultantId === consultant.id
  );

  const dateFields =
    availableDates.length > 0 && scheduleDestination.consultantId
      ? setFetchedDatesToSelectFields(availableDates)
      : [];

  const hourFields = scheduleDestination.date
    ? availableDates
      .filter(({ date }) => {
        const normalizedDate = normalizeScheduleDate(date);
        return normalizedDate.includes(scheduleDestination.date);
      })
      .map(({ date }) => {
        const normalizedDate = normalizeScheduleDate(date);
        const timePart = normalizedDate.split(' ')[1];
        return {
          value: timePart,
          label: timePart.substring(0, 5),
        };
      })
    : [];

  useEffect(() => {
    async function fetchDates() {
      try {
        const response = await api.get(
          `/api/client/schedules/consultant/${scheduleDestination.consultantId}`
        );
        const dates = response.data.data;
        setAvailableDates(dates);
      } catch (err) {
        addToast({
          title: "Erro ao carregar datas disponíveis",
          type: "error",
        });
      }
    }

    if (scheduleDestination.consultantId) {
      fetchDates();
    }
  }, [scheduleDestination.consultantId]);

  useEffect(() => {
    if (open) {
      setVideoCall(!!param.video_call);
      return;
    }

    setOriginSource("");
    setVideoCall(false);
  }, [open, param.video_call]);

  const handleUpdate = async () => {
    if (!canManageSchedules) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    setLoadingRequestToUpdate(true);

    try {
      const schema = Yup.object().shape({
        consultantId: Yup.string().required("Selecione um consultor"),
        date: Yup.string().required("Selecione um dia"),
        hour: Yup.string().required("Selecione uma hora"),
        originSource: Yup.string().required("Selecione a origem"),

      });


      await schema.validate(
        { ...scheduleDestination, originSource },
        { abortEarly: false }
      );

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

      const { consultantId, date, hour } = scheduleDestination;

      const formattedHour = hour.length === 8 ? hour.substring(0, 5) : hour;
      const scheduleDestinationFormatted = `${date}|${formattedHour}|${consultantId}`;

      const response = await api.put(`/api/adm/schedules/${scheduleId}`, {
        schedule: scheduleDestinationFormatted,
        status: 2, // SCHEDULED
        origin_source: originSource,
        video_call: videoCall,
      });

      if (!!response.data.success) {
        addToast({
          title: "Alteração concluída!",
          type: "success",
          description: "O agendamento foi atualizado com sucesso.",
        });

        setTimeout(() => {
          handleRefresh();
          functionToClose();
        }, 800);

      } else {
        throw new Error("Resposta da API não indica sucesso");
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        addToast({
          title: errors[Object.keys(errors)[0]],
          type: "info",
          description: "",
        });
      } else {
        addToast({
          title: getApiErrorToastTitle(err, "Erro ao atualizar agendamento"),
          type: "error",
        });
      }

      setLoadingRequestToUpdate(false);
    }
  };

  function handleChange(identifier: string, value: string | undefined) {
    setScheduleDestination((prevDestination) => ({
      ...prevDestination,
      [identifier]: value || "",
    }));
  }

  return (
    <Modal
      open={open}
      onClose={functionToClose}
      classNames={{
        modal: "modalUpdate",
      }}
      closeIcon={<FiX size={18} color="#000" />}
    >
      <Content>
        <TitleContainer>
          <h3>ALTERAR AGENDAMENTO</h3>
        </TitleContainer>
        <hr />
        <FormContainer>
          <p>Consultor:</p>
          <input value={selectedConsultant?.first_name ?? ""} readOnly />
          <span
            className="iconContainer"
            onMouseDown={() => {
              handleChange("consultantId", undefined);
              setShowConsultantsList(true);
            }}
          >
            <RiArrowLeftRightLine size={24} color="#6ea4ff" />
          </span>
          {consultants.length >= 1 && showConsultantsList ? (
            <OutsideClickHandler
              onOutsideClick={() => {
                setShowConsultantsList(false);
              }}
            >
              <div id="consultants">
                {consultants.map((consultant) => (
                  <button
                    key={consultant.id}
                    type="button"
                    onClick={() => {
                      handleChange("consultantId", consultant.id!.toString());
                      setShowConsultantsList(false);
                    }}
                  >
                    {consultant.first_name}
                  </button>
                ))}
              </div>
            </OutsideClickHandler>
          ) : null}
          <p>Novo dia:</p>
          <Select
            options={dateFields}
            onChange={(e) => handleChange("date", e?.value)}
            placeholder="Escolha uma data"
            noOptionsMessage={() =>
              "Não temos datas disponíveis para agendamento"
            }
          />

          <p>Nova hora:</p>
          <Select
            options={hourFields}
            onChange={(e) => handleChange("hour", e?.value)}
            placeholder="Escolha um horário"
            noOptionsMessage={() =>
              "Não temos horários disponíveis nessa data."
            }
          />

          <p>Origem:</p>
          <Select
            options={ORIGIN_SOURCE_OPTIONS}
            value={
              originSource
                ? ORIGIN_SOURCE_OPTIONS.find((o) => o.value === originSource) ?? null
                : null
            }
            onChange={(e) => setOriginSource((e?.value ?? "") as OriginSource | "")}
            placeholder="Escolha a origem"
            noOptionsMessage={() => "Sem opções"}
          />

          <p>Tipo de visita:</p>
          <CheckboxContainer>
            <input
              checked={videoCall}
              type="checkbox"
              id="video-call-update"
              onChange={(e) => setVideoCall(e.target.checked)}
            />
            <label htmlFor="video-call-update">Visita por videochamada</label>
          </CheckboxContainer>
        </FormContainer>

        <ButtonContainer>
          {loadingRequestToUpdate ? (
            <p>Carregando...</p>
          ) : (
            <button className="btn-edit" onClick={handleUpdate}>
              SALVAR ALTERAÇÕES
            </button>
          )}
          <button className="btn-close" onClick={functionToClose}>
            CANCELAR
          </button>
        </ButtonContainer>
      </Content>
    </Modal>
  );
};

export default ModalUpdate;